import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";
import { KineticHeading } from "../components/site/KineticHeading";
import { Reveal } from "../components/site/Reveal";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useContent } from "../context/ContentContext";
import { api, formatError } from "../lib/api";

const INTERESTS = ["EVENT CORE", "EXPERIENCE CORE", "TECH CORE", "Not sure yet"];
const inputClass =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-white placeholder-white/30 outline-none transition-colors duration-300 focus:border-volt focus:ring-1 focus:ring-volt/40";

export default function Contact() {
  const { site } = useContent();
  const c = site.contact || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [params] = useSearchParams();

  useEffect(() => {
    const pr = params.get("product");
    if (pr) setForm((f) => (f.message ? f : { ...f, message: `I'd like a quote for: ${pr}.` }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message || "Thanks — we'll be in touch.");
      setForm({ name: "", email: "", phone: "", company: "", interest: "", message: "" });
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="mx-auto max-w-[1400px] px-6 pb-28 pt-40 md:px-10 md:pt-48" data-testid="contact-page">
      <div className="mb-6 text-xs font-bold uppercase tracking-[0.25em] text-volt">Contact</div>
      <KineticHeading lines={["Don't be shy.", "Say hello."]}
        className="font-display text-5xl font-semibold uppercase leading-[0.95] tracking-tighter sm:text-7xl lg:text-8xl" />

      <div className="mt-16 grid gap-16 lg:grid-cols-[1.3fr_1fr]">
        <Reveal>
          <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
            <div className="grid gap-5 sm:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm text-white/60">Name *</label>
                <input data-testid="contact-name" className={inputClass} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div>
                <label className="mb-2 block text-sm text-white/60">Email *</label>
                <input data-testid="contact-email" type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="jane@company.com" />
              </div>
              <div>
                <label className="mb-2 block text-sm text-white/60">Phone</label>
                <input data-testid="contact-phone" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="+32 ..." />
              </div>
              <div>
                <label className="mb-2 block text-sm text-white/60">Company</label>
                <input data-testid="contact-company" className={inputClass} value={form.company} onChange={set("company")} placeholder="Company name" />
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm text-white/60">Interested in</label>
              <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
                <SelectTrigger data-testid="contact-interest" className={`${inputClass} h-auto`}>
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="border-white/10 bg-korio-elevated text-white">
                  {INTERESTS.map((i) => (
                    <SelectItem key={i} value={i} className="focus:bg-white/10 focus:text-white">{i}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="mb-2 block text-sm text-white/60">Message *</label>
              <textarea data-testid="contact-message" rows={5} className={inputClass} value={form.message} onChange={set("message")} placeholder="Tell us about your event..." />
            </div>

            <button type="submit" disabled={loading} data-testid="contact-submit"
              className="brand-gradient group inline-flex items-center gap-2 rounded-full px-8 py-4 font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
              {loading ? "Sending..." : "Send message"}
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="rounded-2xl border border-white/10 bg-korio-surface p-8">
            <h2 className="font-display text-2xl font-medium tracking-tight">Or talk directly.</h2>
            <p className="mt-3 text-white/55">We answer the phone. We reply to emails. We still remember what email is for.</p>
            <div className="mt-8 space-y-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40">Email</div>
                <a href={`mailto:${c.email}`} data-testid="contact-info-email" className="font-display text-xl text-white transition-colors hover:text-volt">{c.email}</a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40">Phone</div>
                <a href={c.phoneHref} className="font-display text-xl text-white transition-colors hover:text-volt">{c.phone}</a>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/40">Address</div>
                <div className="mt-1 text-white/70">
                  <div className="text-white">{c.company}</div>
                  {(c.address || []).map((l) => <div key={l}>{l}</div>)}
                  <div className="mt-2 text-white/40">{c.vat}</div>
                </div>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
